import React, {useContext, useRef} from 'react';
import {createStackNavigator} from '@react-navigation/stack';
import Header from '../general/Header';
import PlanList from './PlanList';
import Content from './content/Content';
import DeletePlanButton from './DeletePlanButton';
import PlansContext from '../../contexts/PlansContext';
import {Plan} from '../../database/schemas/plan';
import {Contents} from '../../database/schemas/content';
import {UpdatePlanFailure, UpdatePlanResult} from '../../hooks/usePlans';
import SecureDownload from '../search-download/SecureDownload';
import {PlanSearchResult} from '../../api';

export type PlanAction = {
  type: 'check for updates' | 'delete plan';
  planIds?: string[];
};

type ContentParams = {
  planId: string;
  contentId?: string;
};

export type PlanStackParamList = {
  PlansList: {action: PlanAction} | undefined;
  Content: ContentParams;
  SecureDownload: {
    plan: PlanSearchResult;
    returnRoute: string;
  };
};

type UpdateItems = {
  successItems: string[];
  failedItems: UpdatePlanFailure[];
  passwordItems: UpdatePlanFailure[];
};

const groupUpdateItems = (results: UpdatePlanResult[]): UpdateItems =>
  results.reduce(
    (items, result) => {
      if (typeof result === 'string') {
        items.successItems.push(result);
      } else if (result.passwordRequired) {
        items.passwordItems.push(result);
      } else {
        items.failedItems.push(result);
      }

      return items;
    },
    {
      successItems: [],
      failedItems: [],
      passwordItems: [],
    } as UpdateItems,
  );

const Stack = createStackNavigator<PlanStackParamList>();

const PlanRoutes = () => {
  const context = useContext(PlansContext);
  const updateCheckInFlight = useRef<Promise<void> | null>(null);

  const processPasswordUpdates = async (
    passwordItems: UpdatePlanFailure[],
    navigation: any,
  ) => {
    for (const changedPasswordItem of passwordItems) {
      const shouldUpdate = window.confirm(
        `Password Changed for plan: ${changedPasswordItem.name}\n\n` +
          'A new password is required to update this plan. ' +
          'Select OK to enter it, or Cancel to remove the plan.',
      );

      if (shouldUpdate) {
        if (!changedPasswordItem.plan) {
          context!.sendNotification(
            'alert',
            `Unable to update ${changedPasswordItem.name}`,
          );
          continue;
        }

        navigation.navigate('SecureDownload', {
          plan: changedPasswordItem.plan,
          returnRoute: 'PlansList',
        });

        // Returning from the password screen triggers another update check,
        // which resumes the queue with any plans that are still stale.
        return;
      }

      await context!.plans.deletePlan(changedPasswordItem.planID);
      context!.sendNotification(
        'info',
        `${changedPasswordItem.name} has been removed`,
      );
    }
  };

  const checkForUpdates = (navigation: any): Promise<void> => {
    if (updateCheckInFlight.current) {
      return updateCheckInFlight.current;
    }

    const checkPromise = context!.plans
      .checkForUpdates()
      .then(async ({updated, removed}) => {
        if (updated.length) {
          context!.sendNotification(
            'info',
            `Updating ${updated.length} plan${
              updated.length > 1 ? 's' : ''
            }`,
          );
          const results = await Promise.all(
            updated.map(context!.plans.updatePlan),
          );
          const {successItems, failedItems, passwordItems} = groupUpdateItems(
            results,
          );

          if (successItems.length) {
            context!.sendNotification(
              'info',
              `Finished updating ${successItems.length} plan${
                successItems.length > 1 ? 's' : ''
              }`,
            );
          }

          if (failedItems.length) {
            context!.sendNotification(
              'alert',
              `Failed updating ${failedItems.length} plan${
                failedItems.length > 1 ? 's' : ''
              }`,
            );
          }

          if (passwordItems.length) {
            await processPasswordUpdates(passwordItems, navigation);
          }
        }

        if (removed.length) {
          await Promise.all(
            removed.map(async (plan: Plan) => {
              const name = plan.name;
              try {
                await context!.plans.deletePlan(plan.planID);
                context!.sendNotification(
                  'info',
                  `${name} has been removed`,
                );
              } catch (error) {
                console.log('Error deleting plan', error);
              }
            }),
          );
        }
      })
      .catch(error => console.log('Error checking for updates', error))
      .then(() => undefined)
      .finally(() => {
        updateCheckInFlight.current = null;
      });

    updateCheckInFlight.current = checkPromise;
    return checkPromise;
  };

  // Execute pending actions when arriving on the PlanList route
  // - Delete plan
  const afterRouteToPlanList = ({action}: {action: PlanAction}) => {
    const {type, planIds} = action;
    switch (type) {
      case 'delete plan':
        const name = context!.doa.getDocument<Plan>('Plan', planIds![0])!.name;

        return context!.plans
          .deletePlan(planIds![0])
          .then(() =>
            context!.sendNotification('info', `${name} has been deleted`),
          )
          .catch(error => console.log('Error deleting plan', error));
    }
  };

  return (
    <Stack.Navigator
      initialRouteName="PlansList"
      headerMode="screen"
      screenOptions={{header: (props) => <Header {...props} />}}>
      <Stack.Screen
        name="SecureDownload"
        component={SecureDownload as any}
        options={{headerBackTitle: 'My Plans'}}
      />
      <Stack.Screen
        name="PlansList"
        component={PlanList}
        listeners={({route, navigation}) => ({
          focus: () => checkForUpdates(navigation),
          transitionEnd: () =>
            route.params && afterRouteToPlanList(route.params),
        })}
      />
      <Stack.Screen
        name="Content"
        component={Content}
        options={({route}) => {
          const colors = Contents.headerColors(
            context!.doa,
            route.params?.contentId,
          );
          return {
            headerBackTitle: '',
            headerTintColor: colors.tint,
            headerStyle: {
              backgroundColor: colors.background,
            },
            headerRight: route.params.contentId
              ? undefined
              : () => (
                  <DeletePlanButton
                    planId={route.params.planId}
                    color={colors.tint}
                  />
                ),
          };
        }}
      />
    </Stack.Navigator>
  );
};

export default PlanRoutes;
